import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import QuoteEmailModal from '../components/QuoteEmailModal';
import { getConsultInfo, saveConsultInfo, uploadS3 } from '../services/apiService';
import { generatePdfBlobFromElement } from '../utils/pdfExportUtil';

const ATTACH_PATH = '1/counseling/Schedule/';

function normalizeConsultNos(location) {
  try {
    const stateIds = location?.state?.consultNos;
    if (Array.isArray(stateIds) && stateIds.length) {
      return stateIds
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n) && n > 0);
    }

    const search = location?.search || '';
    const params = new URLSearchParams(search);
    const raw = params.get('ids') || params.get('consultNos') || '';
    if (!raw) return [];
    return raw
      .split(',')
      .map((s) => Number(String(s).trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  } catch {
    return [];
  }
}

function escapeHtml(v) {
  try {
    return String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  } catch {
    return '';
  }
}

function formatNumber(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('ko-KR');
}

function buildTimestampedName(name) {
  try {
    const dot = name.lastIndexOf('.');
    const base = dot > -1 ? name.slice(0, dot) : name;
    const ext = dot > -1 ? name.slice(dot) : '';
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    return `${base}_${stamp}${ext}`;
  } catch {
    return name;
  }
}

function getProductNoFromConsultInfo(info) {
  try {
    const tc = info?.팀컬러;
    const first = Array.isArray(tc) ? tc[0] : tc;
    const digits = first != null ? String(first).replace(/[^0-9]/g, '') : '';
    const n = Number(digits);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function buildFormFromConsultInfo(info) {
  const i = info || {};
  return {
    요청자명: i.요청자명 ?? '',
    요청자휴대폰: i.요청자휴대폰 ?? '',
    요청자이메일: i.요청자이메일 ?? '',
    수배처: i.수배처 ?? null,
    수배처번호: i.수배처번호 ?? null,
    시작일: i.시작일 ?? '',
    종료일: i.종료일 ?? '',
    견적명: i.견적명 ?? '',
    견적내용: i.견적내용 ?? '',
    성인수: i.성인수 ?? 0,
    소아수: i.소아수 ?? 0,
    유아수: i.유아수 ?? 0,
    성인판매가: i.성인판매가 ?? 0,
    소아판매가: i.소아판매가 ?? 0,
    유아판매가: i.유아판매가 ?? 0,
    성인원가: i.성인원가 ?? 0,
    소아원가: i.소아원가 ?? 0,
    유아원가: i.유아원가 ?? 0,
    견적첨부파일리스트: Array.isArray(i.견적첨부파일리스트) ? i.견적첨부파일리스트 : [],
    세부원가목록: Array.isArray(i.세부원가)
      ? i.세부원가.map((row) => ({
          원가항목: row?.원가항목 ?? '',
          성인: Number(row?.성인 ?? 0),
          어린이: Number(row?.어린이 ?? 0),
          영유아: Number(row?.영유아 ?? 0),
        }))
      : [],
  };
}

function resolveConsultStatusCode(info) {
  try {
    const raw = info?.상담상태코드 ?? info?.상담상태 ?? '';
    const digits = raw != null ? String(raw).replace(/[^0-9]/g, '') : '';
    if (digits) return digits;
    const text = String(raw || '').trim();
    if (text === '상담요청') return '420003';
    if (text === '진행중') return '420001';
    if (text === '예약전환') return '420002';
    if (text === '상담취소') return '420999';
    return null;
  } catch {
    return null;
  }
}

function resolveConsultTypeCode(info) {
  try {
    const raw = info?.상담구분코드 ?? info?.상담구분 ?? '';
    const digits = raw != null ? String(raw).replace(/[^0-9]/g, '') : '';
    if (digits) return digits;
    return null;
  } catch {
    return null;
  }
}

function buildCompareElement(infos) {
  const wrapper = document.createElement('div');
  wrapper.style.width = '794px';
  wrapper.style.maxWidth = '794px';
  wrapper.style.background = '#ffffff';
  wrapper.style.color = '#111827';
  wrapper.style.fontFamily = "Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', Arial, sans-serif";
  wrapper.style.padding = '20px';

  const today = new Date().toLocaleDateString('ko-KR');

  const cols = (Array.isArray(infos) ? infos : []).slice(0, 4);

  const columnHeaders = cols
    .map((q, idx) => {
      const title = escapeHtml(q?.견적명 || `견적 ${idx + 1}`);
      const cn = q?.상담번호 != null ? escapeHtml(q.상담번호) : '';
      return `<th style="text-align:center; padding:8px; border:1px solid #e5e7eb; background:#f9fafb; font-weight:700;">${title}${cn ? `<div style=\"font-weight:400; color:#6b7280; font-size:11px; margin-top:2px;\">상담번호: ${cn}</div>` : ''}</th>`;
    })
    .join('');

  const buildRow = (label, getValue) => {
    const tds = cols
      .map((q) => `<td style="padding:8px; border:1px solid #e5e7eb; vertical-align:top; text-align:center;">${escapeHtml(getValue(q) || '-')}</td>`)
      .join('');
    return `<tr><td style="padding:8px; border:1px solid #e5e7eb; font-weight:600; background:#ffffff;">${escapeHtml(label)}</td>${tds}</tr>`;
  };

  const rows = [
    buildRow('여행기간', (q) => (q?.시작일 && q?.종료일 ? `${q.시작일} ~ ${q.종료일}` : q?.시작일 || q?.종료일 || '-')),
    buildRow('인원', (q) => {
      const a = Number(q?.성인수 || 0);
      const c = Number(q?.소아수 || 0);
      const i = Number(q?.유아수 || 0);
      const parts = [];
      parts.push(`성인 ${a}명`);
      if (c > 0) parts.push(`소아 ${c}명`);
      if (i > 0) parts.push(`유아 ${i}명`);
      return parts.join(' / ');
    }),
    buildRow('견적내용', (q) => String(q?.견적내용 || '-')),
    buildRow('성인 1인 판매가', (q) => `${formatNumber(q?.성인판매가)}원`),
    buildRow('소아 1인 판매가', (q) => `${formatNumber(q?.소아판매가)}원`),
    buildRow('유아 1인 판매가', (q) => `${formatNumber(q?.유아판매가)}원`),
    buildRow('총 판매가', (q) => {
      const a = Number(q?.성인수 || 0) * Number(q?.성인판매가 || 0);
      const c = Number(q?.소아수 || 0) * Number(q?.소아판매가 || 0);
      const i = Number(q?.유아수 || 0) * Number(q?.유아판매가 || 0);
      return `${formatNumber(a + c + i)}원`;
    }),
  ].join('');

  wrapper.innerHTML = `
    <div style="border-bottom:2px solid #2563eb; padding-bottom:12px; margin-bottom:16px;">
      <div style="font-size:20px; font-weight:800;">견적 비교서</div>
      <div style="font-size:12px; color:#6b7280; margin-top:4px;">생성일: ${escapeHtml(today)}</div>
    </div>

    <div style="border:1px solid #e5e7eb; border-radius:8px; overflow:hidden;">
      <table style="width:100%; border-collapse:collapse; font-size:13px;">
        <thead>
          <tr>
            <th style="text-align:left; padding:8px; border:1px solid #e5e7eb; background:#f9fafb;">항목</th>
            ${columnHeaders}
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
    </div>
  `;

  return wrapper;
}

export default function QuoteComparePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const consultNos = useMemo(() => normalizeConsultNos(location), [location]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultInfos, setConsultInfos] = useState([]);

  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');

  const [emailOpen, setEmailOpen] = useState(false);

  const defaultRecipientEmail = useMemo(() => {
    try {
      const first = consultInfos?.[0];
      return String(first?.요청자이메일 || '').trim();
    } catch {
      return '';
    }
  }, [consultInfos]);

  const periodLabel = useMemo(() => {
    try {
      const periods = (Array.isArray(consultInfos) ? consultInfos : [])
        .map((q) => (q?.시작일 && q?.종료일 ? `${q.시작일} ~ ${q.종료일}` : q?.시작일 || q?.종료일 || ''))
        .filter(Boolean);
      if (!periods.length) return '-';
      return periods.length === 1 ? periods[0] : `${periods[0]} 외 ${periods.length - 1}건`;
    } catch {
      return '-';
    }
  }, [consultInfos]);

  const compareTitle = useMemo(() => {
    try {
      const names = (Array.isArray(consultInfos) ? consultInfos : [])
        .map((q) => String(q?.견적명 || '').trim())
        .filter(Boolean);
      if (!names.length) return '견적 비교서';
      const base = names[0];
      return names.length === 1 ? base : `${base} 외 ${names.length - 1}건 비교`;
    } catch {
      return '견적 비교서';
    }
  }, [consultInfos]);

  const targetConsultInfoForAttach = useMemo(() => {
    return consultInfos?.[0] || null;
  }, [consultInfos]);

  const onDownloadPdf = async () => {
    if (!pdfUrl) {
      message.warning('먼저 PDF를 생성해주세요.');
      return;
    }
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!consultNos.length) {
        setLoading(false);
        setConsultInfos([]);
        setError('비교할 상담(견적)을 찾을 수 없습니다.');
        return;
      }
      if (consultNos.length < 2) {
        setLoading(false);
        setConsultInfos([]);
        setError('비교는 2개 이상의 상담(견적) 선택이 필요합니다.');
        return;
      }
      if (consultNos.length > 4) {
        setLoading(false);
        setConsultInfos([]);
        setError('비교는 최대 4개까지 지원합니다.');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const list = await Promise.all(
          consultNos.map((cn) =>
            getConsultInfo({ consultNo: cn })
              .then((info) => ({ ...(info || {}), 상담번호: cn }))
              .catch(() => ({ 상담번호: cn }))
          )
        );
        if (cancelled) return;
        const normalized = (Array.isArray(list) ? list : []).filter(Boolean);
        setConsultInfos(normalized);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || '상담(견적) 정보를 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [consultNos]);

  const onGeneratePdfAndUpload = async () => {
    if (pdfGenerating) return;
    if (!consultInfos || consultInfos.length < 2) {
      message.warning('비교할 상담(견적)이 없습니다.');
      return;
    }

    // Popup blockers: open a blank tab synchronously on user gesture,
    // then redirect to the generated PDF URL once ready.
    let pdfWindow = null;
    try {
      const loadingHtml = `<!doctype html>
<html lang="ko">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>PDF 생성중...</title>
    <style>
      body { font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Malgun Gothic', Arial, sans-serif; padding: 20px; color: #111827; }
      .card { max-width: 520px; margin: 40px auto; border: 1px solid #e5e7eb; border-radius: 12px; padding: 18px; }
      .title { font-weight: 800; font-size: 16px; margin-bottom: 8px; }
      .desc { font-size: 13px; color: #6b7280; line-height: 1.6; }
      .bar { height: 6px; background: #e5e7eb; border-radius: 999px; overflow: hidden; margin-top: 14px; }
      .bar > div { height: 100%; width: 40%; background: #2563eb; animation: move 1.2s infinite ease-in-out; }
      @keyframes move { 0% { transform: translateX(-100%); } 100% { transform: translateX(250%); } }
    </style>
  </head>
  <body>
    <script>try{window.opener=null;}catch(e){};</script>
    <div class="card">
      <div class="title">비교견적서 PDF 생성중...</div>
      <div class="desc">PDF를 생성하고 업로드하는 중입니다. 완료되면 자동으로 PDF가 열립니다.</div>
      <div class="bar"><div></div></div>
    </div>
  </body>
</html>`;
      const loadingUrl = `data:text/html;charset=utf-8,${encodeURIComponent(loadingHtml)}`;
      // NOTE: Don't use 'noopener' feature here; some browsers will return null window handle.
      pdfWindow = window.open(loadingUrl, '_blank');
      if (pdfWindow && pdfWindow.document) {
        try {
          pdfWindow.document.title = 'PDF 생성중...';
          pdfWindow.document.body.innerHTML = '<div style="font-family: sans-serif; padding: 16px;">PDF 생성중...</div>';
        } catch {
          // ignore cross-origin restrictions
        }
      }
    } catch {
      pdfWindow = null;
    }

    try {
      setPdfGenerating(true);
      const element = buildCompareElement(consultInfos);
      const blob = await generatePdfBlobFromElement(element);
      const safeBase = String(compareTitle || '견적비교서').replace(/["<>:|?*\\/]/g, ' ');
      const stampedName = buildTimestampedName(`TRP__COMPARE__${safeBase}.pdf`);

      const { url } = await uploadS3({ file: blob, fileName: stampedName, filePath: ATTACH_PATH });
      setPdfUrl(url || '');
      setPdfFileName(stampedName);

      if (url && pdfWindow && !pdfWindow.closed) {
        try {
          pdfWindow.location.replace(url);
        } catch {
          // ignore
        }
      } else if (url && !pdfWindow) {
        message.info('PDF가 생성되었습니다. 팝업 차단으로 자동 열기가 제한될 수 있습니다. "PDF 열기" 버튼을 사용해주세요.');
      }

      // Attach to the first consult for record keeping (best-effort)
      try {
        const target = targetConsultInfoForAttach;
        const consultNo = target?.상담번호 != null ? Number(target.상담번호) : null;
        const productNo = getProductNoFromConsultInfo(target);
        if (consultNo && productNo) {
          const baseForm = buildFormFromConsultInfo(target);
          const pathOnly = `${ATTACH_PATH}${stampedName}`;
          const newItem = { 첨부파일번호: 0, 파일명: stampedName, 첨부파일경로: pathOnly, url, 자동생성: true };
          const nextForm = {
            ...baseForm,
            견적첨부파일리스트: Array.isArray(baseForm.견적첨부파일리스트)
              ? [...baseForm.견적첨부파일리스트, newItem]
              : [newItem],
          };

          // IMPORTANT: saveConsultInfo has defaults (status/type) that can overwrite the original.
          // Only save if we can resolve existing codes safely.
          const consultStatusCode = resolveConsultStatusCode(target);
          const consultTypeCode = resolveConsultTypeCode(target);
          if (consultStatusCode && consultTypeCode) {
            await saveConsultInfo({
              consultNo,
              productNo,
              form: nextForm,
              consultStatusCode,
              consultTypeCode,
            });
          }
        }
      } catch (e) {
        // best-effort only
        console.warn('비교견적서 첨부 저장 실패(무시):', e);
      }

      message.success('비교견적서 PDF 생성이 완료되었습니다.');
    } catch (e) {
      console.error('비교견적서 PDF 생성 실패:', e);
      message.error(e?.message || 'PDF 생성에 실패했습니다.');

      try {
        if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
      } catch {
        // ignore
      }
    } finally {
      setPdfGenerating(false);
    }
  };

  const emailAttachments = useMemo(() => {
    try {
      const base = targetConsultInfoForAttach ? buildFormFromConsultInfo(targetConsultInfoForAttach) : null;
      const list = base?.견적첨부파일리스트;
      const baseList = Array.isArray(list) ? list : [];
      if (!pdfUrl) return baseList;
      const compareItem = {
        첨부파일번호: 0,
        파일명: pdfFileName || '비교견적서.pdf',
        첨부파일경로: pdfFileName ? `${ATTACH_PATH}${pdfFileName}` : '',
        url: pdfUrl,
        자동생성: true,
      };
      return [compareItem, ...baseList];
    } catch {
      return [];
    }
  }, [targetConsultInfoForAttach, pdfUrl, pdfFileName]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[17px] font-bold text-gray-900">견적 비교</div>
          <div className="text-sm text-gray-500">{consultNos.length ? `선택 ${consultNos.length}건` : ''}</div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center h-9 px-3.5 text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
          >
            목록으로
          </button>
          <button
            type="button"
            onClick={onGeneratePdfAndUpload}
            disabled={pdfGenerating || loading || !!error}
            className={`inline-flex items-center justify-center h-9 px-3.5 text-sm font-semibold rounded-lg text-white ${pdfGenerating || loading || error ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}
          >
            {pdfGenerating ? 'PDF 생성중...' : '비교견적서 PDF 생성'}
          </button>
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            disabled={!pdfUrl}
            className={`inline-flex items-center justify-center h-9 px-3.5 text-sm font-semibold rounded-lg ${!pdfUrl ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
          >
            이메일 발송
          </button>
          <button
            type="button"
            onClick={onDownloadPdf}
            disabled={!pdfUrl}
            className={`inline-flex items-center justify-center h-9 px-3.5 text-sm font-semibold rounded-lg ${!pdfUrl ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
          >
            PDF 열기
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl p-5 overflow-hidden">
            <div className="pb-4">
              <div className="text-[17px] font-bold text-gray-900">비교표</div>
              <div className="text-xs text-gray-500">PDF에 포함되는 요약 비교입니다.</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[920px] w-full table-fixed divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="h-10 px-4 text-left text-[13px] font-medium text-gray-500 w-[14rem] first:rounded-l-lg">항목</th>
                    {consultInfos.map((q) => (
                      <th key={q?.상담번호} className="px-4 py-2 text-center text-[13px] font-medium text-gray-500 last:rounded-r-lg">
                        <div className="text-sm font-bold text-gray-900 truncate">{q?.견적명 || '-'}</div>
                        <div className="text-[11px] text-gray-500">상담번호: {q?.상담번호}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">여행기간</td>
                    {consultInfos.map((q) => (
                      <td key={q?.상담번호} className="px-4 py-3 text-sm text-gray-700 text-center">
                        {q?.시작일 && q?.종료일 ? `${q.시작일} ~ ${q.종료일}` : q?.시작일 || q?.종료일 || '-'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">인원</td>
                    {consultInfos.map((q) => (
                      <td key={q?.상담번호} className="px-4 py-3 text-sm text-gray-700 text-center">
                        성인 {Number(q?.성인수 || 0)}명
                        {Number(q?.소아수 || 0) > 0 ? ` / 소아 ${Number(q?.소아수 || 0)}명` : ''}
                        {Number(q?.유아수 || 0) > 0 ? ` / 유아 ${Number(q?.유아수 || 0)}명` : ''}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">견적내용</td>
                    {consultInfos.map((q) => (
                      <td key={q?.상담번호} className="px-4 py-3 text-sm text-gray-700 text-center whitespace-pre-wrap break-words">
                        {q?.견적내용 || '-'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">총 판매가</td>
                    {consultInfos.map((q) => {
                      const total =
                        Number(q?.성인수 || 0) * Number(q?.성인판매가 || 0) +
                        Number(q?.소아수 || 0) * Number(q?.소아판매가 || 0) +
                        Number(q?.유아수 || 0) * Number(q?.유아판매가 || 0);
                      return (
                        <td key={q?.상담번호} className="px-4 py-3 text-sm font-semibold text-gray-900 text-center">
                          {formatNumber(total)}원
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">성인 1인 판매가</td>
                    {consultInfos.map((q) => (
                      <td key={q?.상담번호} className="px-4 py-3 text-sm text-gray-700 text-center">{formatNumber(q?.성인판매가)}원</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {pdfUrl && (
            <div className="text-xs text-gray-500">
              생성된 PDF: {pdfFileName ? <span className="font-mono">{pdfFileName}</span> : pdfUrl}
            </div>
          )}
        </>
      )}

      <QuoteEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        recipientEmail={defaultRecipientEmail}
        quoteName={compareTitle}
        period={periodLabel}
        pdfUrl={pdfUrl}
        attachments={emailAttachments}
      />
    </div>
  );
}
